import {
  apiErrorSchema,
  CSRF_COOKIE_NAME,
  CSRF_HEADER_NAME,
  type ApiErrorCodeId,
  type FieldIssue,
} from '@firegame/shared';
import { t } from '../i18n/index.js';

/** Methodes qui ne modifient rien : elles n'ont pas besoin du jeton anti-CSRF. */
const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

/**
 * Lit le jeton anti-CSRF depose par le serveur.
 * Ce cookie est volontairement accessible au JavaScript : c'est le principe de
 * la double soumission (cookie + en-tete).
 */
function readCsrfToken(): string | null {
  const prefix = `${CSRF_COOKIE_NAME}=`;
  for (const part of document.cookie.split(';')) {
    const trimmed = part.trim();
    if (trimmed.startsWith(prefix)) {
      return decodeURIComponent(trimmed.slice(prefix.length));
    }
  }
  return null;
}

/**
 * Client HTTP unique du frontend.
 *
 * Regles :
 * - toutes les requetes partent en meme origine, avec les cookies de session ;
 * - toute reponse d'erreur est convertie en `ApiClientError` porteuse du code
 *   applicatif, ce qui permet aux ecrans de reagir sur le code et jamais sur le
 *   texte du message ;
 * - les reponses sont validees par le schema Zod partage avant d'etre utilisees.
 */

export class ApiClientError extends Error {
  readonly code: ApiErrorCodeId | 'NETWORK_ERROR' | 'INVALID_RESPONSE';
  readonly status: number;
  readonly issues: FieldIssue[];
  readonly requestId: string | null;

  constructor(params: {
    code: ApiErrorCodeId | 'NETWORK_ERROR' | 'INVALID_RESPONSE';
    message: string;
    status: number;
    issues?: FieldIssue[];
    requestId?: string | null;
  }) {
    super(params.message);
    this.name = 'ApiClientError';
    this.code = params.code;
    this.status = params.status;
    this.issues = params.issues ?? [];
    this.requestId = params.requestId ?? null;
  }

  /** Message d'erreur associe a un champ de formulaire, s'il existe. */
  issueFor(path: string): string | undefined {
    return this.issues.find((issue) => issue.path === path)?.message;
  }
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  /** Delai maximal avant abandon, en millisecondes. */
  timeoutMs?: number;
}

const DEFAULT_TIMEOUT_MS = 15_000;

/** Prefixe commun de toutes les routes ; Nginx et Vite proxifient « /api ». */
const API_BASE = '/api';

interface ResponseParser<T> {
  parse: (value: unknown) => T;
}

export async function request<T>(
  path: string,
  parser: ResponseParser<T>,
  options: RequestOptions = {},
): Promise<T> {
  const { method = 'GET', body, timeoutMs = DEFAULT_TIMEOUT_MS } = options;

  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), timeoutMs);
  if (options.signal) {
    options.signal.addEventListener('abort', () => controller.abort(), { once: true });
  }

  const headers: Record<string, string> = { Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  if (!SAFE_METHODS.has(method)) {
    const csrfToken = readCsrfToken();
    if (csrfToken) headers[CSRF_HEADER_NAME] = csrfToken;
  }

  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      method,
      // Le cookie de session accompagne chaque requete de meme origine.
      credentials: 'same-origin',
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: controller.signal,
    });
  } catch {
    throw new ApiClientError({
      code: 'NETWORK_ERROR',
      message: t().errors.network,
      status: 0,
      requestId: null,
    });
  } finally {
    window.clearTimeout(timeout);
  }

  const rawText = await response.text();
  let payload: unknown = null;
  if (rawText.length > 0) {
    try {
      payload = JSON.parse(rawText);
    } catch {
      payload = null;
    }
  }

  if (!response.ok) {
    const parsedError = apiErrorSchema.safeParse(payload);
    if (parsedError.success) {
      throw new ApiClientError({
        code: parsedError.data.error.code,
        message: parsedError.data.error.message,
        status: response.status,
        issues: parsedError.data.error.issues ?? [],
        requestId: parsedError.data.requestId,
      });
    }
    throw new ApiClientError({
      code: 'INVALID_RESPONSE',
      message: t().errors.unexpected,
      status: response.status,
    });
  }

  try {
    return parser.parse(payload);
  } catch {
    // Une reponse valide en HTTP mais non conforme au contrat est traitee comme
    // une panne : mieux vaut un message clair qu'un ecran a moitie rempli.
    throw new ApiClientError({
      code: 'INVALID_RESPONSE',
      message: t().errors.unexpected,
      status: response.status,
    });
  }
}
